import React, { createContext, useContext, ReactNode } from 'react';
import { useNotification } from '../hooks/useNotification';
import NotificationDialog from '../components/notificationDialog';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

interface ToastContextData {
  showToast: (message: string, severity?: NotificationSeverity, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextData | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { notification, showSuccess, showError, showWarning, showInfo, showNotification, hideNotification } = useNotification();

  // Wrapper para manter compatibilidade com a API antiga
  const showToast = (message: string, severity: NotificationSeverity = 'info', duration?: number) => {
    showNotification(message, severity);
  };

  return (
    <ToastContext.Provider value={{
      showToast,
      showSuccess,
      showError,
      showWarning,
      showInfo
    }}>
      {children}
      <NotificationDialog
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </ToastContext.Provider>
  );
};
